// Tách nền + phóng to ảnh NGAY TRÊN TRÌNH DUYỆT bằng Canvas — không tốn tiền AI, chạy offline.
//
// Vì sao ép AI tô nền xanh rồi tự tách, thay vì bảo nó "nền trong suốt":
// mô hình sinh ảnh rất hay trả về nền TRẮNG ĐẶC dù prompt ghi transparent. Nền xanh đơn sắc
// thì tách bằng thuật toán chắc chắn 100%, không phụ thuộc may rủi.
//
// Ba tầng, theo đúng thứ tự — bỏ tầng nào cũng sai:
//   1. Ảnh đã trong suốt sẵn  -> KHÔNG tách (tránh xoá nhầm chủ thể đen/trắng)
//   2. Dò nền có đơn sắc không -> không đơn sắc thì giữ nguyên, thà để nền còn hơn thủng ảnh
//   3. Nền xanh chroma -> khử xanh có xử lý viền; nền đơn sắc khác -> chroma-key theo màu

export const CHROMA_HEX = '#00b140';

// Nền xanh chroma: G trội hẳn so với R và B.
export const isChromaGreen = (r, g, b) => g > 90 && g - Math.max(r, b) > 45;

const hexToRgb = (hex) => {
  const h = String(hex).replace('#', '');
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
};
const toHex = (c) => '#' + [c.r, c.g, c.b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');

// Lấy 4 khối góc (mỗi khối 4% cạnh ngắn) — nền thật gần như luôn nằm ở góc.
function cornerBlocks(w, h) {
  const S = Math.max(2, Math.floor(Math.min(w, h) * 0.04));
  return { S, blocks: [[0, 0], [w - S, 0], [0, h - S], [w - S, h - S]] };
}

// Tầng 1: ảnh đã trong suốt sẵn chưa? (đọc alpha, không đọc màu)
function cornerAvgAlpha(data, w, h) {
  const { S, blocks } = cornerBlocks(w, h);
  let a = 0, n = 0;
  for (const [ox, oy] of blocks) {
    for (let y = oy; y < oy + S; y++) for (let x = ox; x < ox + S; x++) { a += data[(y * w + x) * 4 + 3]; n++; }
  }
  return n ? a / n : 255;
}

// Tầng 2: 4 góc có cùng một màu không? Lệch nhiều -> nền không đơn sắc -> trả null.
function detectSolidBg(data, w, h) {
  const { S, blocks } = cornerBlocks(w, h);
  const corners = [];
  for (const [ox, oy] of blocks) {
    let r = 0, g = 0, b = 0, n = 0;
    for (let y = oy; y < oy + S; y++) for (let x = ox; x < ox + S; x++) {
      const i = (y * w + x) * 4; r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
    }
    corners.push({ r: r / n, g: g / n, b: b / n });
  }
  const avg = corners.reduce((a, c) => ({ r: a.r + c.r / 4, g: a.g + c.g / 4, b: a.b + c.b / 4 }), { r: 0, g: 0, b: 0 });
  const spread = corners.reduce((a, c) => a + Math.abs(c.r - avg.r) + Math.abs(c.g - avg.g) + Math.abs(c.b - avg.b), 0);
  return spread < 90 ? toHex(avg) : null;
}

// Tầng 3a — nền xanh: xoá hẳn pixel xanh; pixel xanh NHẠT ở viền thì giảm alpha dần
// và kéo kênh G xuống, nếu không ảnh sẽ có viền xanh răng cưa.
function greenScreen(data) {
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const other = Math.max(r, b), excess = g - other;
    if (isChromaGreen(r, g, b)) { data[i + 3] = 0; continue; }
    if (g > 80 && excess > 12) {
      data[i + 3] = Math.min(data[i + 3], Math.max(0, Math.round(255 * (1 - (excess - 12) / 33))));
      if (g > other) data[i + 1] = other;   // khử ám xanh
    }
  }
}

// Tầng 3b — nền đơn sắc màu khác: xoá theo khoảng cách màu, có vùng mềm để viền không gắt.
function chromaKey(data, bg, tolerance = 72) {
  const t2 = tolerance * tolerance, soft = tolerance * 1.7, s2 = soft * soft;
  for (let i = 0; i < data.length; i += 4) {
    const dr = data[i] - bg.r, dg = data[i + 1] - bg.g, db = data[i + 2] - bg.b;
    const d2 = dr * dr + dg * dg + db * db;
    if (d2 <= t2) data[i + 3] = 0;
    else if (d2 <= s2) {
      const t = (Math.sqrt(d2) - tolerance) / (soft - tolerance);
      data[i + 3] = Math.min(data[i + 3], Math.round(255 * t));
    }
  }
}

const loadBitmap = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Không tải được ảnh'));
  img.src = src;
});

/**
 * Xử lý 1 ảnh: tách nền (tuỳ chọn) + phóng to.
 * @param {string} src dataURL hoặc URL ảnh
 * @param {{autoBg?:boolean, upscale?:number, maxSize?:number}} opts
 * @returns {Promise<{dataUrl:string, width:number, height:number, removed:boolean, reason:string}>}
 */
export async function processImage(src, opts = {}) {
  const { autoBg = true, upscale = 1, maxSize = 4096 } = opts;
  const img = await loadBitmap(src);
  const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;

  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);

  let removed = false, reason = 'Không tách nền';
  if (autoBg) {
    let id;
    try { id = ctx.getImageData(0, 0, w, h); }
    catch { return { dataUrl: cv.toDataURL('image/png'), width: w, height: h, removed: false, reason: 'Ảnh khác nguồn, trình duyệt chặn đọc điểm ảnh' }; }

    if (cornerAvgAlpha(id.data, w, h) <= 32) {
      reason = 'Ảnh đã trong suốt sẵn — giữ nguyên';
    } else {
      const bgHex = detectSolidBg(id.data, w, h);
      if (!bgHex) {
        reason = 'Nền không đơn sắc — không tách được bằng thuật toán';
      } else {
        const rgb = hexToRgb(bgHex);
        if (isChromaGreen(rgb.r, rgb.g, rgb.b)) { greenScreen(id.data); reason = 'Đã tách nền xanh chroma'; }
        else { chromaKey(id.data, rgb); reason = `Đã tách nền đơn sắc ${bgHex}`; }
        ctx.putImageData(id, 0, 0);
        removed = true;
      }
    }
  }

  const scale = Math.max(1, Math.min(4, upscale || 1));
  const outW = Math.min(maxSize, Math.round(w * scale));
  const outH = Math.min(maxSize, Math.round(h * scale));
  if (outW === w && outH === h) {
    return { dataUrl: cv.toDataURL('image/png'), width: w, height: h, removed, reason };
  }
  const out = document.createElement('canvas');
  out.width = outW; out.height = outH;
  const octx = out.getContext('2d');
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(cv, 0, 0, outW, outH);
  return { dataUrl: out.toDataURL('image/png'), width: outW, height: outH, removed, reason };
}

// Đọc file/URL ảnh thành dataURL (dùng khi cần gửi ảnh gốc lên backend).
export const toDataUrl = (blob) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(fr.result);
  fr.onerror = () => reject(new Error('Không đọc được ảnh'));
  fr.readAsDataURL(blob);
});
